export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | null | object;
}

function resolveApiBaseUrl(): string {
  const value = import.meta.env.VITE_API_BASE_URL?.trim();
  if (value && value.length > 0) {
    return value.replace(/\/$/, "");
  }

  if (typeof window === "undefined") {
    throw new Error("VITE_API_BASE_URL is required");
  }

  const { protocol, hostname } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//localhost:5000`;
  }

  if (hostname.startsWith("mob.")) {
    return `${protocol}//api.${hostname}`;
  }

  throw new Error("Unable to resolve API base URL");
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const apiBaseUrl = resolveApiBaseUrl();
  const { headers, body, credentials, ...rest } = options;
  const resolvedHeaders = new Headers(headers ?? {});
  let resolvedBody: BodyInit | null | undefined;

  if (body != null && !(body instanceof FormData) && !(body instanceof URLSearchParams) && typeof body !== "string" && !(body instanceof Blob)) {
    resolvedHeaders.set("Content-Type", "application/json");
    resolvedBody = JSON.stringify(body);
  } else {
    resolvedBody = body as BodyInit | null | undefined;
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...rest,
    headers: resolvedHeaders,
    body: resolvedBody,
    credentials: credentials ?? "include"
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json().catch(() => null) : await response.text().catch(() => "");

  if (!response.ok) {
    throw new ApiError(extractErrorMessage(payload) ?? `Request failed with status ${response.status}`, response.status, payload);
  }

  return payload as T;
}

function extractErrorMessage(payload: unknown): string | null {
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return null;
}
